<script setup>
import { computed, watch, inject, ref, onMounted } from 'vue'
import { useVModel } from '#/general/useVModel'
const { isBrowser, application } = inject('Shared')
const emit = defineEmits(['update:showModal'])
const props = defineProps({
  showModal: Boolean,
  seamless: Boolean,
  persistent: Boolean,
  fullScreen: Boolean,
  maximized: Boolean,
  duration: Number,
  transition: String,
  position: String,
  fullWidth: Boolean,
  fixed: Boolean,
  filterColor: {
    type: String,
    default: 'rgba(0, 0, 0, 0.2)'
  }
})
const computedPosition = computed(() => {
  return props.position ? props.position : 'center'
})

const visible = useVModel(props, 'showModal')

const computedTransition = computed(() => {
  if (props.transition === 'float') {
    return `float-${props.position}`
  } return `${props.transition ? props.transition : 'fade'}`
})

watch(() => props.showModal, val => {
  if (application === 'WIDGET') {
    return
  }
  if (isBrowser && props.fullScreen) {
    const body = document.body
    body.style.overflowY = 'hidden'
    if (val && !props.seamless) {
      body.style.height = '100vh'
    } else {
      setTimeout(() => {
        body.style.overflowY = 'initial'
      }, props.duration ? props.duration : 200)
    }
  }
})

const closeDialog = (event) => {
  if (!props.persistent && !props.seamless && event.target && event.target.className && event.target.className.includes && event.target.className.includes('modal--content')) {
    emit('update:showModal', false)
  }
}

const contentClassObject = computed(() => ({
  'content-maximized': props.maximized
}))
const modalClassObject = computed(() => ({
  'position-right': computedPosition.value === 'right' && props.seamless,
  'position-left': computedPosition.value === 'left' && props.seamless,
  'position-top': computedPosition.value === 'top' && props.seamless,
  'position-bottom': computedPosition.value === 'bottom' && props.seamless,
  'position-center': computedPosition.value === 'center' && props.seamless,
  'dialog-full-screen': props.showModal && (props.maximized || props.fullScreen || !props.seamless),
  'dialog-full-width': props.fullWidth,
  'absolute-position': props.showModal && !props.fixed,
  'fixed-position': props.showModal && props.fixed,
  'content-hidden': !props.showModal,
  'content-visible': props.showModal
}))

const zIndex = ref(1001)
watch(() => props.showModal, val => {
  if (isBrowser) {
    let count
    if (application === 'WIDGET') {
      if (process.env.NODE_ENV === 'production') {
        const shadow = document.querySelector('#app').shadowRoot
        count = shadow.querySelectorAll('.modal--content').length
      } else {
        count = document.getElementsByClassName('modal--content').length
      }
    } else {
      count = document.getElementsByClassName('modal--content').length
    }
    zIndex.value = 1001 + (count * 2)
    let layout
    if (application === 'WIDGET') {
      if (process.env.NODE_ENV === 'production') {
        const shadow = document.querySelector('#app').shadowRoot
        layout = shadow.querySelector('#appContainer')
      } else {
        layout = document.getElementById('appContainer')
      }
    } else {
      layout = document.getElementById('layout')
    }
    if (val && props.fullScreen) {
      const dialogFilter = document.createElement('div')
      dialogFilter.id = 'dialogFilter'
      dialogFilter.style.width = '100%'
      dialogFilter.style.height = '100%'
      dialogFilter.style.position = 'absolute'
      dialogFilter.style.top = '0'
      dialogFilter.style.backdropFilter = 'blur(4px)'
      dialogFilter.style.zIndex = String(zIndex.value - 1)
      dialogFilter.style.backgroundColor = props.filterColor
      layout.appendChild(dialogFilter)
    } else {
      removeFilter()
    }
  }
})

function removeFilter () {
  if (application === 'WIDGET') {
    if (process.env.NODE_ENV === 'production') {
      setTimeout(() => {
        document.querySelector('#app')?.shadowRoot?.querySelector('#dialogFilter')?.remove()
      }, 200)
    } else {
      setTimeout(() => {
        document.getElementById('dialogFilter')?.remove()
      }, 200)
    }
  } else {
    setTimeout(() => {
      document.getElementById('dialogFilter')?.remove()
    }, 200)
  }
}

let to
if (application === 'WIDGET') {
  if (process.env.NODE_ENV === 'production') {
    const shadow = document.querySelector('#app').shadowRoot
    to = shadow.querySelector('#appContainer')
  } else {
    to = '.content'
  }
} else {
  to = '.appContainer'
}

const mounted = ref(false)
onMounted(() => {
  mounted.value = true
  history.pushState = (f => function pushState () {
    const ret = f.apply(this, arguments)
    window.dispatchEvent(new Event('pushstate'))
    window.dispatchEvent(new Event('locationchange'))
    return ret
  })(history.pushState)

  history.replaceState = (f => function replaceState () {
    const ret = f.apply(this, arguments)
    window.dispatchEvent(new Event('replacestate'))
    window.dispatchEvent(new Event('locationchange'))
    return ret
  })(history.replaceState)

  window.addEventListener('locationchange', () => {
    if (application === 'WEB_APP') { visible.value = false }
  })
})
</script>

<template lang="pug">
div(v-if="to && mounted")
  teleport(:to="to")
    transition(:name="computedTransition")
      .modal(v-if="showModal" :class="modalClassObject" role="dialog" :style="`z-index: ${zIndex} !important`")
        .modal--bg(@click="closeDialog($event)")
          transition(:name="computedTransition")
            .modal--content(v-if="showModal" :class="contentClassObject")
              slot(v-if="props.showModal" name="modal-content")
</template>

<style lang="sass">
.modal
  overflow-x: hidden
  .modal--bg
    height: 100%
    width: 100%
  .modal--bg
    background-color: transparent !important
.modal, .modal--bg, .modal--content
  transition: .3s all
  will-change: transform, opacity, left, top, right, bottom
  z-index: 1001

//position
.position-left
  left: 0
  top: 50%
  transform: translateY(-50%)
.position-right
  right: 0
  top: 50%
  transform: translateY(-50%)
.position-top
  top: 0
  left: 50%
  transform: translateX(-50%)
.position-bottom
  bottom: 0
  left: 50%
  transform: translateX(-50%)
.position-center
  top: 50%
  left: 50%
//transform: translate(-50%, -50%)
//-webkit-transform: translate(-50%, -50%)
//-o-transform: translate(-50%, -50%)
//-ms-transform: translate(-50%, -50%)
//-moz-transform: translate(-50%, -50%)
//fade
.fade-enter-from, .fade-leave-to
  opacity: 0
.fade-leave-from, .fade-enter-to
  opacity: 1

//grow & shrink
.grow-enter-from, .grow-leave-to
  transform: scale(0)
  -webkit-transform: scale(0)
  -o-transform: scale(0)
  -ms-transform: scale(0)
  -moz-transform: scale(0)
.grow-enter-to, .grow-leave-from
  transform: scale(1)
  -webkit-transform: scale(1)
  -o-transform: scale(1)
  -ms-transform: scale(1)
  -moz-transform: scale(1)

//float
.float-right-enter-from, .float-right-leave-to
  right: -100% !important
.float-right-leave-from, .float-right-enter-to
  right: 0 !important
.float-left-enter-from, .float-left-leave-to
  left: -100% !important
.float-left-leave-from, .float-left-enter-to
  left: 0 !important
.float-top-enter-from, .float-top-leave-to
  top: -100% !important
.float-top-leave-from, .float-top-enter-to
  top: 0 !important
.float-bottom-enter-from, .float-bottom-leave-to
  bottom: -100% !important
.float-bottom-leave-from, .float-bottom-enter-to
  bottom: 0 !important
.dialog-full-screen
  top: 0
  width: 100% !important
  height: 100% !important
.dialog-full-height
  height: 100% !important
.dialog-full-width
  width: 100% !important
.content-maximized
  display: flex
  justify-content: center
  align-items: center
  width: 100% !important
  height: 100% !important
.fixed-position
  position: fixed !important
.absolute-position
  position: absolute
.content-hidden
  visibility: hidden
.content-visible
  visibility: visible
</style>
